/**
 * Synthetische, anonyme Beispieldaten für Tests und Oberflächenentwicklung.
 * Keine echten Kunden, Kennungen oder Angebote.
 */
import type { Article, Design, Embed, Gallery, Item, JobStatus, LegacyUserV2, Member, Tenant, TenantStats } from './entities.js';
import type { RuleSet } from './rules.js';

const T0 = '2026-10-23T12:00:00Z';

export const fixtureTenant: Tenant = {
  tenantId: '900001',
  legacyU: '0123456789ab',
  displayName: 'Beispielhändler',
  accountStatus: 'active',
  migrationState: 'schatten',
  ebay: {
    type: 'auth-n-auth',
    secretRef: '/pat/dev/secret/tenant/900001/ebay-token',
    expiresAt: '2027-06-30T00:00:00Z',
    expiresAtSource: 'fetch-token',
    ebayUserName: 'beispielhaendler',
    primarySiteId: 77,
  },
  features: { designColors: true, crossSelling: true, liveUpdate: true },
  sync: { lastReconcileComplete: false, deltaIntervalMinutes: 15 },
  nextGalleryNumber: 90001,
  createdAt: T0,
  updatedAt: T0,
};

export const fixtureMember: Member = { tenantId: '900001', userId: 'cognito-sub-0001', role: 'owner', createdAt: T0 };

export const fixtureLegacyUser: LegacyUserV2 = {
  emailLower: 'beispiel@example.invalid',
  accounts: [{ tenantId: '900001', passwordHash: '$2b$12$abcdefghijklmnopqrstuuABCDEFGHIJKLMNOPQRSTUVWXYZ012345', passwordHashMode: 'exact', passwordTooShort: false, language: 'de', accountStatus: 'active', groupTenantIds: [] }],
  loginTenantId: '900001',
  locked: false,
};

export const fixtureJobStatus: JobStatus = {
  tenantId: '900001',
  type: 'sync.reconcile',
  state: 'idle',
  lastRunAt: T0,
  lastSuccessAt: T0,
  nextRunAt: '2026-10-24T03:00:00Z',
  counters: { pages: 1, itemsSeen: 3 },
};

export const fixtureStats: TenantStats = { tenantId: '900001', activeItems: 2, activeItemsBySite: { '77': 2 }, itemsWithReviseError: 0, pendingRevisions: 1, computedAt: T0 };

export const fixtureEmbed: Embed = {
  tenantId: '900001',
  ebayItemId: '310000000001',
  mode: 'full',
  state: 'pending',
  stateSince: T0,
  origin: 'worker',
  articleId: 'art-1',
  articleVersion: 2,
  designId: 'des-1',
  designVersion: 1,
  galleries: { 'gal-1': { selectionHash: 'a'.repeat(64) } },
  revisionsToday: 0,
};

export const fixtureItems: Item[] = [
  {
    tenantId: '900001',
    ebayItemId: '310000000001',
    site: 77,
    title: 'Beispielartikel Alpha 10er Set',
    listingType: 'fixed',
    price: { amount: '19.99', currency: 'EUR' },
    pictureUrl: 'https://i.ebayimg.example/alpha.jpg',
    startTime: '2026-10-01T10:00:00Z',
    endTime: '2026-10-31T10:00:00Z',
    ended: false,
    categoryId: '20740',
    storeCategoryId: '1',
    sku: 'ALPHA-10',
    quantity: 25,
    quantitySold: 3,
    titleNorm: 'beispielartikel alpha 10er set',
    hasVariations: false,
    lastSeenAt: T0,
    articleId: 'art-1',
  },
  {
    tenantId: '900001',
    ebayItemId: '310000000002',
    site: 77,
    title: 'Beispielartikel Beta 5er Set',
    listingType: 'fixed',
    price: { amount: '9.50', currency: 'EUR' },
    startTime: '2026-10-05T10:00:00Z',
    endTime: '2026-11-04T10:00:00Z',
    ended: false,
    categoryId: '20740',
    storeCategoryId: '1',
    hasVariations: true,
    lastSeenAt: T0,
  },
  {
    tenantId: '900001',
    ebayItemId: '310000000003',
    site: 3,
    title: 'Example item Gamma',
    listingType: 'fixed',
    price: { amount: '12.00', currency: 'GBP' },
    startTime: '2026-09-20T10:00:00Z',
    endTime: '2026-10-20T10:00:00Z',
    ended: true,
    categoryId: '20740',
    hasVariations: false,
    lastSeenAt: T0,
  },
];

export const fixtureGallery: Gallery = {
  tenantId: '900001',
  galleryId: 'gal-1',
  legacyG: '16842',
  site: 77,
  name: 'Zubehör',
  settings: {
    galleryType: 0,
    insertMode: 'bottom',
    maxItems: 10,
    height: 2,
    titleLines: 3,
    showPrice: true,
    showInfoText: true,
    showAuctionType: true,
    bigPicture: false,
    fontStyleButtonBold: false,
    linkTarget: '_blank',
    drawStyle: 0,
    groupNoByTitle: false,
    language: 'de',
    align: 'center',
    colors: { c1: 'f9f9f9', c3: 'ffffff', i1: 'FFFFFF', i2: '000000', i3: 'dddddd' },
    sameSiteOnly: true,
    sortMode: 0,
    randomize: false,
  },
  version: 1,
  deleted: false,
  createdAt: T0,
  updatedAt: T0,
};

export const fixtureRuleSet: RuleSet = {
  astVersion: 1,
  tenantId: '900001',
  galleryId: 'gal-1',
  version: 1,
  scripts: [
    {
      scriptId: 'scr-1',
      lines: [
        {
          lineNo: 1,
          direction: 'source-to-target',
          left: { alternatives: [{ terms: [{ kind: 'word', value: 'Alpha' }] }] },
          right: { alternatives: [{ terms: [{ kind: 'word', value: 'Beta' }] }] },
        },
      ],
      unsupportedLines: [],
    },
  ],
  rules: [{ ruleId: 'r-1', kind: 'same-store-category', priority: 1 }],
  updatedAt: T0,
};

export const fixtureArticle: Article = {
  tenantId: '900001',
  articleId: 'art-1',
  title: 'Beispielartikel Alpha',
  sku: 'ALPHA-10',
  designId: 'des-1',
  blocks: [
    { id: 'b1', type: 'header', data: { text: 'Beispielartikel Alpha' } },
    { id: 'b2', type: 'text', data: { text: 'Beschreibungstext.', options: { columnCount: 1 } } },
    { id: 'b3', type: 'picture', data: { list: [{ mediaId: 'med-1', label: 'Vorderseite' }], options: { format: 'list' } } },
  ],
  version: 3,
  publishedVersion: 2,
  deleted: false,
  createdAt: T0,
  updatedAt: T0,
};

export const fixtureDesign: Design = {
  tenantId: '900001',
  designId: 'des-1',
  name: 'Standard blau',
  layout: '3line_header',
  variant: 3,
  colors: { font_family: 'Arial', font_size: 2, body_bg: { bg_type: 1, color1: 'f9f9f9' }, color_1: { active: 1, font_color: '444444' }, color_1_bg: { bg_type: 0, color1: 'ffffff' } },
  logo: { type: 'text', text: 'Beispielhändler' },
  boxes: [{ id: 'x1', type: 'text', headline: 'Versand', text: 'Versand innerhalb von 24 h.', column: 2, order: 0, legacyId: '4711' }],
  links: [],
  trust: [],
  isTemplate: false,
  version: 1,
  deleted: false,
  createdAt: T0,
  updatedAt: T0,
};
